// Port of SuiteModel/Tags.swift: as many words or phrases as the writer likes on one document ("flashback, needs
// research"), where Label and Status hold one choice each. A tags field (type `tags`; every project has the standard one,
// `tags`) keeps them as one text value, the names separated by commas — what a journal's Tags field always held, and
// what a build from before tags reads as a text field. There's no list of tags kept anywhere: the project's tags are the
// ones its documents have (`tagCounts`).
//
// Two devices that each change a document's tags before syncing leave two values at the key, of which Automerge shows
// one: the tags are everything either has (`fragmentTags`), until the next change stores one list (`setTags`).
import type { FragmentRecord } from './model/documents.ts';
import { swiftCompare } from './model/order.ts';

const collator = new Intl.Collator('en', { sensitivity: 'accent' });
/** `localizedCaseInsensitiveCompare`. */
const caseInsensitiveOrder = (a: string, b: string) => collator.compare(a, b);

export const TAGS_FIELD = 'tags';

/** `Tags.normalized`: trimmed, inner runs of whitespace made one space. */
const normalized = (tag: string) => tag.split(/\p{White_Space}+/u).filter((w) => w !== '').join(' ');

/** `Tags.key`: what two spellings of one tag share ("Needs Research", "needs  research"). */
export const tagKey = (tag: string) => normalized(tag).toLowerCase();

function unique(tags: string[]): string[] {
  const seen = new Set<string>();
  return tags.filter((t) => t !== '' && !seen.has(tagKey(t)) && (seen.add(tagKey(t)), true));
}

/** `Tags.parse`: the tags in a stored value, in order — trimmed, empty ones and repeats (however they're capitalised)
 *  left out. A tag can't contain a comma or a line break. */
export const parseTags = (text: string): string[] => unique(text.split(/[,\n\r\u0085\u2028\u2029\v\f]/).map(normalized));

/** `Tags.joined`: the value stored for these tags ("" for none). */
export const joinTags = (tags: readonly string[]): string => unique(tags.flatMap(parseTags)).join(', ');

/** `Tags.contains`. */
export const hasTag = (tags: readonly string[], tag: string): boolean => tags.some((t) => tagKey(t) === tagKey(tag));

/** `Tags.adding`. */
export const addingTag = (tag: string, tags: readonly string[]): string[] => unique([...tags, ...parseTags(tag)]);

/** `Tags.removing`. */
export function removingTag(tag: string, tags: readonly string[]): string[] {
  const gone = new Set(parseTags(tag).map(tagKey));
  return tags.filter((t) => !gone.has(tagKey(t)));
}

/** `Tags.Count`: a tag in use and how many documents have it. */
export interface TagCount { tag: string; count: number }

/** `Tags.counts`: the tags these documents have between them, by name, each spelled as most of its documents spell it
 *  (ties to the spelling that sorts first, so every device shows the same one). */
export function tagCounts(documents: Iterable<readonly string[]>): TagCount[] {
  const spellings = new Map<string, Map<string, number>>();
  for (const tags of documents) {
    for (const tag of unique([...tags])) {
      const spelled = spellings.get(tagKey(tag)) ?? new Map<string, number>();
      spelled.set(tag, (spelled.get(tag) ?? 0) + 1);
      spellings.set(tagKey(tag), spelled);
    }
  }
  const out: TagCount[] = [];
  for (const spelled of spellings.values()) {
    let name = '', most = 0, count = 0;
    for (const [tag, n] of spelled) {
      count += n;
      if (n > most || (n === most && swiftCompare(tag, name) < 0)) { name = tag; most = n; }
    }
    out.push({ tag: name, count });
  }
  return out.sort((a, b) => caseInsensitiveOrder(a.tag, b.tag) || swiftCompare(a.tag, b.tag));
}

/** `FragmentDocument.tags(_:)`: the document's tags in a tags field — the value Automerge shows, then any other value
 *  the key holds at once (another device's, not yet made one with it). */
export function fragmentTags(f: Pick<FragmentRecord, 'metadata' | 'metadataConflicts'>, field = TAGS_FIELD): string[] {
  const shown = f.metadata[field];
  const values = shown?.type === 'text' ? [shown.value] : [];
  return unique([...values, ...(f.metadataConflicts[field] ?? [])].flatMap(parseTags));
}
